// Eskom PARCC — CES (Compliance Evaluation Sheet) capture orchestration.
//
// The PURE heart of Task 19 (no Firebase, no React): given a reviewer's filled-in
// Compliance Evaluation Sheet — the chosen procurement route, the per-criterion
// rows, free-form additional findings, and the transaction context — produce the
// FOUR normalised write payloads that the save sequence persists, in the exact
// shapes the `dbWrite` wrappers accept:
//
//   1. `transaction`  → TransactionInsertInput  (rolled-up compliance + review fields)
//   2. `workingPaper` → WorkingPaperInput        (reviewStatus / complianceLevel / notes / capturedBy)
//   3. `cesRows`      → CesRowInput[]            (one per non-blank criterion, for replaceCesRowsForTx)
//   4. `findings`     → FindingInsertInput[]     (one per non-compliant criterion + one per
//                                                 additional-findings line, each classified)
//
// Domain logic is REUSED, never reimplemented:
//   • `seedCesRows`            (domain/ces) — seed the editable criteria for a route.
//   • `cesMapStatusToRegister` (domain/ces) — a CES status → register compliance level.
//   • `cesOverallCompliance`   (domain/ces) — roll the per-criterion statuses up.
//   • `classifyFinding`        (domain/classify) — anchors / route / confidence / matchedRules / level.
//
// Parity with the monolith's `cesSaveTransaction` (git 6810c83:index.html ~4085):
//   • A criterion is "non-compliant" when it has a finding OR a status that is not
//     blank / Compliant / Not-Applicable. Each such row → one finding whose text is
//     `criterion + finding + (Action: action)`, classified, with compliance from
//     `cesMapStatusToRegister(status, risk)` (falling back to the rollup when that
//     maps to "Pending Review").
//   • The rollup level is written as the strict-rule override
//     (`complianceLevelOverride`), which the register's `effectiveCompliance`
//     reads as the authoritative transaction level.
// Divergence from the monolith (deliberate, per Task 19): additional findings are
// split into one classified finding PER non-blank line, and are emitted even when
// criterion findings exist (the monolith collapsed them into a single finding only
// when there were no criterion findings).

import { seedCesRows, cesMapStatusToRegister, cesOverallCompliance } from '../../domain/ces';
import { classifyFinding, type TxContext } from '../../domain/classify';
import { isFindingLevel } from '../../domain/dashboard';
import type { CesRoute } from '../../domain/enums';
import type { WorkingPaperTemplate } from '../../domain/workingPapers/templateTypes';
import type {
  TransactionInsertInput,
  WorkingPaperInput,
  CesRowInput,
  FindingInsertInput,
} from '../../lib/dbWrite';

/** A reviewer-editable CES criterion line (the modal's working row). */
export interface CesCriterionRow {
  seq: number;
  stage: string;
  criterion: string;
  /** A `CES_STATUS_OPTIONS` value; '' is the unset default. */
  status: string;
  finding: string;
  /**
   * Set ONLY when the reviewer confirmed an AI proposal for this row, so the
   * engine's output (its PFMA section, the citations, its own confidence, the
   * model) survives onto the saved finding instead of being discarded. Absent on
   * a hand-typed row — existing behaviour is unchanged.
   */
  ai?: {
    pfmaSection?: string | null;
    citations?: { doc: string; page: number; quote: string }[];
    confidence?: number;
    model?: string | null;
    runId?: string | null;
  };
}

/**
 * A reviewer-added structured gap (Transversal / Multisource templates only —
 * the sheet's "Gaps Identified / Compliance level per GAP / Reference of
 * contravention" block). Each gap becomes an ordinary CES row under a "Gaps
 * Identified" stage, its contravention reference folded into the criterion text
 * ({@link formatGapCriterion}) — so gaps flow through cesRows, findings, the
 * rollup and both exports with no schema change.
 */
export interface CesGapRow {
  description: string;
  /** A `CES_STATUS_OPTIONS` value. */
  status: string;
  /** The "Reference of contravention" cell; folded into the criterion at capture. */
  contravention: string;
}

/** Fold a gap's contravention reference into the single criterion column the capture stores. */
export function formatGapCriterion(description: string, contravention: string): string {
  const d = description.trim();
  const c = contravention.trim();
  return c ? `${d}  (Contravention: ${c})` : d;
}

/** The editable transaction context a CES capture carries (new or existing tx). */
export interface CesTxFields {
  dataset?: string;
  division?: string;
  supplier?: string;
  value?: number;
  valueDisplay?: string;
  /** Defaults to the chosen route when absent (the monolith records the route as the mechanism). */
  mechanism?: string;
  year?: string;
  period?: string;
  link?: string;
  csdNumber?: string;
  recordLabel?: string;
  baseNo?: string;
  gapsStatus?: string;
  // Grey working-paper header fields (R3 #1): the locked population trio is
  // carried through so a capture never clobbers them; the reviewer-entered
  // OpenText fields persist what the CES page collected.
  bu?: string;
  briefDescription?: string;
  buyer?: string;
  contractNumber?: string;
  prNumber?: string;
  procurementManager?: string;
  endUser?: string;
}

/** The full input the capture orchestration consumes. */
export interface CesCaptureInput {
  route: CesRoute;
  referenceNo: string;
  rows: readonly CesCriterionRow[];
  /** Free-form text; split into one classified finding per non-blank line. */
  additionalFindings: string;
  capturedByEmail: string;
  /** Free-text name of the ACTUAL reviewer (owner feedback #8) — the coordinator
   * capturing on their behalf is `capturedByEmail`. Optional. */
  reviewerName?: string;
  /** ISO capture timestamp, injected by the caller so this builder stays pure. */
  capturedDate?: string;
  tx: CesTxFields;
  /** An explicit compliance override; when set it wins over the row-derived rollup. */
  complianceOverride?: string;
  /** Review status to persist; defaults to 'Reviewed' (a capture is a completed review). */
  reviewStatus?: string;
  /** Structured gaps (Transversal / Multisource); appended as "Gaps Identified" rows. */
  gaps?: readonly CesGapRow[];
}

/** The four normalised write payloads, in the order the save sequence consumes them. */
export interface CesCapture {
  transaction: TransactionInsertInput;
  workingPaper: WorkingPaperInput;
  cesRows: CesRowInput[];
  findings: FindingInsertInput[];
}

/**
 * Seed the editable criteria rows for a route (delegates to the domain
 * `seedCesRows`, dropping the read-only `transactionId`/`anchor` shape so the
 * modal works with plain {@link CesCriterionRow} working state). seq is 1-based.
 */
export function seedCriteriaRows(route: CesRoute): CesCriterionRow[] {
  return seedCesRows(route, '').map((r) => ({
    seq: r.seq,
    stage: r.stage,
    criterion: r.criterion,
    status: '',
    finding: '',
  }));
}

/**
 * Seed the editable criteria from a per-mechanism working-paper template (owner
 * feedback points 5 + 6). The criterion group becomes the stage header and the
 * sub-criteria text the criterion. The reviewer sets each criterion's status
 * (which encodes the compliance category), and `cesMapStatusToRegister(status)`
 * suggests the rule-book level on save. (The old hidden "Risk" carrier of the
 * template default was removed with the Risk field — owner feedback 5.)
 */
export function seedCriteriaRowsFromTemplate(template: WorkingPaperTemplate): CesCriterionRow[] {
  return template.criteria.map((c) => ({
    seq: c.seq,
    stage: c.group || template.mechanism,
    criterion: c.label,
    status: '',
    finding: '',
  }));
}

/**
 * A criterion row counts as "non-compliant" for the NOTES BLOB: any row the
 * reviewer annotated or rated non-compliant. Kept deliberately broader than
 * {@link isNonCompliantStatus} so compliant-row observations stay readable in
 * `workingPaperNotes`/`updatedFindings` — safe for the transaction rollup
 * because the blob classifier is only consulted when a transaction has no
 * stored level, no override and no finding lines, and every CES save writes
 * the rollup level.
 */
function isNonCompliantCriterion(row: CesCriterionRow): boolean {
  const finding = row.finding.trim();
  const status = row.status.trim();
  if (finding) return true;
  if (!status) return false;
  return !/^(compliant|not-applicable)$/i.test(status);
}

/**
 * A row produces a FINDING only when its OWN status is a non-compliant pick
 * (R5 #3): a note on a compliant / not-applicable / unrated procedure is an
 * observation, not a finding line, and must not count in the findings register
 * or the open-gaps figures. The note still persists on the CES row and in the
 * notes blob. "Non-compliant" is the register's canonical rule — a mapped
 * level that carries a risk weight ({@link isFindingLevel}) — so the generator
 * and `reportableFindings` can never disagree about what counts.
 */
function isNonCompliantStatus(status: string): boolean {
  return isFindingLevel(cesMapStatusToRegister(status));
}

/** A criterion row carries content worth persisting (status or finding). */
function rowHasContent(row: CesCriterionRow): boolean {
  return Boolean(row.status.trim() || row.finding.trim());
}

/**
 * The text the CLASSIFIER reads for a criterion row: procedure + note combined,
 * so anchor/rule detection keeps the procedure's vocabulary as signal. The
 * STORED finding text is the reviewer's note alone (R3 #2) — see the findings
 * loop in {@link buildCesCapture}.
 */
function criterionClassifierText(row: CesCriterionRow): string {
  return [row.criterion, row.finding.trim()]
    .filter((part) => part && part.trim() !== '')
    .join('\n');
}

/**
 * Build the four normalised CES capture write payloads. Pure: no Firebase, no
 * React, no mutation — just the typed inputs the save sequence will persist.
 */
export function buildCesCapture(input: CesCaptureInput): CesCapture {
  const { route, referenceNo, additionalFindings, capturedByEmail, reviewerName, capturedDate, tx } = input;

  // Structured gaps (Transversal / Multisource) become ordinary "Gaps Identified"
  // rows appended after the criteria, their contravention ref folded into the
  // criterion. A RATED gap is a first-class non-compliance (it produces a finding
  // and feeds the rollup like any other row); every DESCRIBED gap is persisted as
  // a CES row regardless of rating, so a reviewer's typed gap is never lost.
  const maxCriterionSeq = input.rows.reduce((m, r) => Math.max(m, r.seq), 0);
  const gapRows: CesCriterionRow[] = (input.gaps ?? [])
    .filter((g) => g.description.trim() !== '')
    .map((g, i) => ({
      seq: maxCriterionSeq + 1 + i,
      stage: 'Gaps Identified',
      criterion: formatGapCriterion(g.description, g.contravention),
      status: g.status,
      finding: '',
    }));
  const rows: readonly CesCriterionRow[] = gapRows.length ? [...input.rows, ...gapRows] : input.rows;

  // The mechanism the monolith records is the chosen route (unless the caller
  // supplied one explicitly for an existing transaction).
  const mechanism = tx.mechanism ?? route;
  const reviewStatus = input.reviewStatus ?? 'Reviewed';

  // The classifier reads the transaction's mechanism + value to detect the route.
  const ctx: TxContext = { mechanism, value: tx.value };

  // --- Rollup compliance ---------------------------------------------------
  // cesOverallCompliance(rows, override) layers any explicit override on top of
  // the per-criterion roll-up. Only rows the reviewer touched contribute.
  const statuses = rows
    .filter(rowHasContent)
    .map((r) => ({ status: r.status }));
  const rollup = cesOverallCompliance(statuses, input.complianceOverride);

  // --- CES rows ------------------------------------------------------------
  // Every touched criterion (untouched seeded rows are dropped) PLUS every
  // described gap (persisted even if unrated), in the exact CesRowInput shape
  // replaceCesRowsForTx accepts.
  const cesRows: CesRowInput[] = [...input.rows.filter(rowHasContent), ...gapRows].map((r) => ({
    seq: r.seq,
    stage: r.stage,
    criterion: r.criterion,
    status: r.status,
    finding: r.finding,
  }));

  // --- Findings ------------------------------------------------------------
  const findings: FindingInsertInput[] = [];

  // 1. One finding per criterion whose OWN status is non-compliant (R5 #3 —
  //    compliant/N-A/unrated procedures never generate finding lines; their
  //    notes live on the CES rows and in the notes blob). The stored `text` is
  //    the REVIEWER'S NOTE (R3 #2 — the register shows what the reviewer
  //    wrote, not the procedure); the procedure travels separately on
  //    `criterion`. A status-only row (no note) falls back to the criterion so
  //    `text` is never blank. The classifier still reads procedure + note
  //    combined. The compliance is always the row's own mapped level — never
  //    the transaction rollup, which stamped transaction-wide non-compliance
  //    onto individual procedure lines.
  for (const r of rows) {
    if (!isNonCompliantStatus(r.status)) continue;
    const note = r.finding.trim();
    const text = note || r.criterion;
    const auto = classifyFinding(criterionClassifierText(r), ctx);
    const compliance = cesMapStatusToRegister(r.status);
    findings.push({
      text,
      criterion: r.criterion || undefined,
      source: `Compliance Evaluation Sheet: ${route}`,
      legacyStatus: r.status || undefined,
      anchors: auto.anchors,
      suggestedCompliance: compliance,
      finalCompliance: compliance,
      rationale: `Checklist result: ${r.status || 'Pending Review'}.`,
      // The engine's own confidence when this verdict came from a confirmed AI
      // proposal, so the exported Confidence column stops silently reporting the
      // rulebook classifier's number under the engine's name.
      confidence: r.ai?.confidence !== undefined ? String(r.ai.confidence) : auto.confidence,
      route: auto.route,
      matchedRules: auto.matchedRules,
      regulatoryRefs: auto.references,
      effectiveStatus: r.status || 'Pending Review',
      // AI provenance — present only when a reviewer confirmed a proposal for
      // this row. The PFMA section maps to the deliverable's PFMA-Impact column;
      // the citations are the evidence pointer the whole trust argument rests on.
      aiPfmaSection: r.ai?.pfmaSection ?? undefined,
      aiCitations:
        r.ai?.citations && r.ai.citations.length > 0 ? JSON.stringify(r.ai.citations) : undefined,
      aiModel: r.ai?.model ?? undefined,
      aiRunId: r.ai?.runId ?? undefined,
      seeded: false,
    });
  }

  // 2. One classified finding per non-blank additional-findings line.
  const additionalLines = additionalFindings
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
  for (const line of additionalLines) {
    const auto = classifyFinding(line, ctx);
    findings.push({
      text: line,
      source: `Compliance Evaluation Sheet: ${route}`,
      legacyStatus: 'Additional comments',
      anchors: auto.anchors,
      suggestedCompliance: auto.level,
      finalCompliance: auto.level,
      rationale: auto.label || 'Additional comment captured from the Compliance Evaluation Sheet.',
      confidence: auto.confidence,
      route: auto.route,
      matchedRules: auto.matchedRules,
      regulatoryRefs: auto.references,
      effectiveStatus: 'Additional comments',
      seeded: false,
    });
  }

  // --- Summary findings text (for the working-paper notes / updated findings) --
  const summary = rows
    .filter(isNonCompliantCriterion)
    .map(
      (r) =>
        `${r.seq}. ${r.criterion}\nResult: ${r.status || 'Pending Review'}` +
        (r.finding.trim() ? `\nFinding: ${r.finding.trim()}` : ''),
    )
    .join('\n\n');
  const additionalText = additionalLines.join('\n');
  const notes = [summary, additionalText].filter(Boolean).join('\n\n');

  // --- Transaction ---------------------------------------------------------
  const transaction: TransactionInsertInput = {
    referenceNo,
    dataset: tx.dataset || undefined,
    recordLabel: tx.recordLabel || undefined,
    baseNo: tx.baseNo || undefined,
    division: tx.division || undefined,
    supplier: tx.supplier || undefined,
    csdNumber: tx.csdNumber || undefined,
    value: tx.value,
    valueDisplay: tx.valueDisplay || undefined,
    mechanism,
    year: tx.year || undefined,
    period: tx.period || undefined,
    link: tx.link || undefined,
    gapsStatus: tx.gapsStatus || undefined,
    // Grey header fields (R3 #1) — locked carry-through + the OpenText four.
    bu: tx.bu || undefined,
    briefDescription: tx.briefDescription || undefined,
    buyer: tx.buyer || undefined,
    contractNumber: tx.contractNumber || undefined,
    prNumber: tx.prNumber || undefined,
    procurementManager: tx.procurementManager || undefined,
    endUser: tx.endUser || undefined,
    // The rolled-up level is the strict-rule override the register reads as
    // authoritative; complianceLevel mirrors it for surfaces that read it directly.
    complianceLevel: rollup,
    complianceLevelOverride: rollup,
    initialFindings: 'Captured through Compliance Evaluation Sheet',
    updatedFindings: notes || undefined,
    findingText: notes || undefined,
    workingPaperNotes: notes || undefined,
    reviewStatus,
    // Dual-write the boolean review flag alongside reviewStatus: a CES capture with
    // a 'Reviewed' status is a completed review. Honoured only on the UPDATE path
    // (the insert mutation has no `reviewed` column — see dbWrite), so a re-capture
    // of an existing transaction persists it; a brand-new insert simply omits it.
    reviewed: /^reviewed$/i.test(reviewStatus.trim()),
    reviewerEmail: capturedByEmail || undefined,
    // Capture date on the transaction so the Transactions detail panel + exports
    // show when it was captured (owner feedback #8, Aug 2026).
    capturedDate: capturedDate || undefined,
  };

  // --- Working paper -------------------------------------------------------
  const workingPaper: WorkingPaperInput = {
    reviewStatus,
    complianceLevel: rollup,
    findingText: notes || undefined,
    workingPaperNotes: notes || undefined,
    capturedByEmail: capturedByEmail || undefined,
    reviewerEmail: capturedByEmail || undefined,
    // The actual reviewer's name (distinct from the coordinator's email above).
    reviewerName: reviewerName || undefined,
    capturedDate: capturedDate || undefined,
  };

  return { transaction, workingPaper, cesRows, findings };
}
